import { debounce } from "@mongez/reinforcements";
import { action } from "framework/config/action";
import packageJson from "package.json";
import type { ErrorLogData } from "../services";
import { useRouteTracker } from "./use-route-tracker.hook";
import { __DEV__ } from "framework/config/environment";

const ignoredErrors = [
  "Failed to read the 'localStorage'",
  "turbo-stream",
  "socket hang up",
  "manifest version mismatch",
];

export function useLogError() {
  const submit = action("/log/errors");
  const { getNavigationHistory } = useRouteTracker();

  const logError = debounce(
    async (error: Error | string, additionalData?: Record<string, any>) => {
      try {
        const message = typeof error === "string" ? error : error.message;
        const stack = typeof error === "object" ? error.stack : undefined;

        if (typeof window === "undefined" || __DEV__) return;

        for (const errorMessage of ignoredErrors) {
          if (message.includes(errorMessage)) return;
        }

        const errorData: ErrorLogData = {
          message,
          stack,
          source: "frontend",
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          additionalData: {
            ...additionalData,
            version: packageJson.version,
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
            screen: {
              width: screen.width,
              height: screen.height,
            },
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            // Add referer information
            referer: document.referrer || null,
            // Add navigation history
            navigationHistory: getNavigationHistory(),
          },
        };

        await submit(errorData);
      } catch (submitError) {
        console.error("Failed to log error:", submitError);
        // Don't throw error to avoid breaking user experience
      }
    },
    1000,
  );

  return {
    logError,
  };
}
